import React, { useEffect, useRef } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries } from "lightweight-charts";
import { utcTickFormatter } from "../../lib/tz";

const sma = (c, n) => c.map((_, i) => (i < n - 1 ? null : c.slice(i - n + 1, i + 1).reduce((s, x) => s + x, 0) / n));
const ema = (c, n) => { const k = 2 / (n + 1); let p = c[0]; return c.map((x) => (p = x * k + p * (1 - k))); };
const rsi = (c, n = 14) => {
  let g = 0, l = 0; const out = [null];
  for (let i = 1; i < c.length; i++) {
    const d = c[i] - c[i - 1], up = Math.max(d, 0), dn = Math.max(-d, 0);
    if (i <= n) { g += up / n; l += dn / n; out.push(i === n ? 100 - 100 / (1 + g / (l || 1e-9)) : null); continue; }
    g = (g * (n - 1) + up) / n; l = (l * (n - 1) + dn) / n; out.push(100 - 100 / (1 + g / (l || 1e-9)));
  }
  return out;
};

const line = (rows, vals) => rows.map((r, i) => (vals[i] == null ? null : { time: r.time, value: vals[i] })).filter(Boolean);

export const FloorChart = ({ data, panes = false, height = 300, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data?.candles?.length) return;
    const rows = data.candles, close = rows.map((r) => r.close);
    const chart = createChart(ref.current, {
      height, autoSize: true, localization: { locale: "en-US" }, layout: { background: { color: "transparent" }, textColor: "#9fe8f0", panes: { separatorColor: "#123" } },
      grid: { vertLines: { color: "#0d1a2e" }, horzLines: { color: "#0d1a2e" } }, timeScale: { borderColor: "#123", tickMarkFormatter: utcTickFormatter }, rightPriceScale: { borderColor: "#123" },
    });
    const cs = chart.addSeries(CandlestickSeries, { upColor: "#34d399", downColor: "#fb7185", borderVisible: false, wickUpColor: "#34d399", wickDownColor: "#fb7185" });
    cs.setData(rows);
    chart.addSeries(LineSeries, { color: "#FACC15", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(line(rows, sma(close, 20)));
    chart.addSeries(LineSeries, { color: "#E879F9", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(line(rows, sma(close, 50)));
    [["entry", "#3B82F6"], ["target", "#10B981"], ["stop", "#EF4444"]].forEach(([k, color]) => data[k] && cs.createPriceLine({ price: data[k], color, lineWidth: 2, title: k.toUpperCase() }));
    if (panes) {
      chart.addSeries(LineSeries, { color: "#38BDF8", lineWidth: 1 }, 1).setData(line(rows, rsi(close)));
      const m = ema(close, 12).map((x, i) => x - ema(close, 26)[i]), sig = ema(m, 9);
      chart.addSeries(HistogramSeries, { color: "#818CF8" }, 2).setData(rows.map((r, i) => ({ time: r.time, value: m[i] - sig[i], color: m[i] >= sig[i] ? "#10B98199" : "#F43F5E99" })));
      chart.addSeries(LineSeries, { color: "#F59E0B", lineWidth: 1 }, 2).setData(line(rows, m));
      chart.addSeries(LineSeries, { color: "#94A3B8", lineWidth: 1 }, 2).setData(line(rows, sig));
      const ps = chart.panes(); ps[0]?.setStretchFactor(3); ps[1]?.setStretchFactor(1); ps[2]?.setStretchFactor(1);
    }
    chart.timeScale().fitContent();
    return () => chart.remove();
  }, [data, panes, height]);
  return <div ref={ref} style={{ height }} className="w-full" data-testid={testid} />;
};
